import { z } from 'zod';
import type { ContactListQuery, ContactStage } from '@yrp/shared';
import {
  OptionalTextSearchParam,
  PositiveIntegerSearchParam,
  enumSearchParam,
  searchValidator,
} from '../../lib/search-params.js';

/**
 * `/crm` (the contact list) search params. Imported by router.tsx, so this
 * module stays in the entry chunk: zod and the search-param helpers only, and
 * `@yrp/shared` as types (the `satisfies` checks below keep the lists equal to
 * the shared enums at compile time; contact-list-search.test.ts checks them at
 * run time too).
 *
 * - `list` (manual list id) or `smart` (smart list) — mutually exclusive;
 *   when both arrive, `list` wins and `smart` is dropped.
 * - `type`, `stage`, `sort` — refinements (replace, not a Back step).
 *
 * Never in this URL: search text, tag/source text (history state `crmQ`,
 * `crmTag`, `crmSource`) and the admin "All agents" scope (history state
 * `crmAll`).
 */

export const CONTACT_SMART_LISTS = [
  'hot_leads',
  'new_this_week',
  'stale_30d',
  'missing_email',
  'mls_yuma_members',
  'mls_tucson_members',
  'mls_inactive_members',
] as const satisfies ReadonlyArray<NonNullable<ContactListQuery['smartList']>>;
export type ContactSmartList = (typeof CONTACT_SMART_LISTS)[number];

export const CONTACT_LIST_STAGES = [
  'new',
  'contacted',
  'qualified',
  'nurture',
  'closed',
  'lost',
] as const satisfies ReadonlyArray<ContactStage>;

/** Every sort except the default (`activity_desc`, which is the absent key). */
export const CONTACT_LIST_SORTS = [
  'updated_desc',
  'created_desc',
  'name_asc',
  'name_desc',
] as const satisfies ReadonlyArray<Exclude<ContactListQuery['sort'], 'activity_desc'>>;
export type ContactListSort = (typeof CONTACT_LIST_SORTS)[number];

// Compile-time: no shared value is missing from the lists above.
type MissingFrom<TAll, TListed> = Exclude<TAll, TListed>;
const _smartListsComplete: [MissingFrom<NonNullable<ContactListQuery['smartList']>, ContactSmartList>] extends [never] ? true : never = true;
const _stagesComplete: [MissingFrom<ContactStage, (typeof CONTACT_LIST_STAGES)[number]>] extends [never] ? true : never = true;
const _sortsComplete: [MissingFrom<Exclude<ContactListQuery['sort'], 'activity_desc'>, ContactListSort>] extends [never] ? true : never = true;
void _smartListsComplete;
void _stagesComplete;
void _sortsComplete;

/** Contact type option values are agent-defined strings, capped like the DB column (varchar 32). */
export const CONTACT_TYPE_MAX_LENGTH = 32;

export const ContactListSearchSchema = z.object({
  list: PositiveIntegerSearchParam,
  smart: enumSearchParam(z.enum(CONTACT_SMART_LISTS)),
  type: OptionalTextSearchParam(CONTACT_TYPE_MAX_LENGTH),
  stage: enumSearchParam(z.enum(CONTACT_LIST_STAGES)),
  sort: enumSearchParam(z.enum(CONTACT_LIST_SORTS)),
});

const parseContactListSearch = searchValidator(ContactListSearchSchema);

/**
 * `validateSearch` for `/crm`. Never throws; unknown keys are stripped.
 *
 * A key that arrived with an invalid value comes back as `key: undefined`
 * rather than missing, on purpose: TanStack builds `useSearch()` as
 * `{ ...rawSearch, ...validated }`, so only an explicit undefined stops the raw
 * value from reaching the page. The same applies to `smart` when `list` wins.
 */
export function contactListSearch(search: Record<string, unknown>): z.output<typeof ContactListSearchSchema> {
  const parsed = parseContactListSearch(search);
  if (parsed.list !== undefined && parsed.smart !== undefined) return { ...parsed, smart: undefined };
  return parsed;
}

export type ContactListSearch = ReturnType<typeof contactListSearch>;
